import { GridRowModel } from "@mui/x-data-grid-premium";
import React, { useMemo } from "react";
import { useState, useRef, useEffect } from "react";
import { useSettingsContext } from "src/core/settings/useSettings";
import { STORE_USER_EDITS } from "src/shared/environment";
import { getNestedValue } from "src/core/settings/settings";
import tableHelper from "src/core/tables/tableHelper";

export const useTableEdits = (apiRef, rows, tableName, idPath: string[], rowSelectionModel, setExternalRows?: React.Dispatch<React.SetStateAction<any[]>>) => {
  const { settings, setSetting } = useSettingsContext();

  const [allRows, setAllRows] = useState(rows ?? []);
  const allRowsRef = useRef<any[]>(rows ?? []);
  const [rowModes, setRowModes] = useState({});
  const newRowToEditRef = useRef(null);
  const originalRows = useMemo<any>(() => rows, [rows]);

  const processRowUpdateRef = useRef<((newRow: any, oldRow: any) => any) | null>(null);
  const processRowUpdateImplRef = useRef<((newRow: any, oldRow: any) => any) | null>(null);
  const defaultProcessRowUpdateRef = useRef<((newRow: any, oldRow: any) => any) | null>(null);

  const storeUserEdits = STORE_USER_EDITS;

  const initialChanged = storeUserEdits ? getNestedValue(settings, ["TableChangedRows", tableName], []) : [];
  const [hasUnsavedRows, setHasUnsavedRows] = useState((initialChanged?.length ?? 0) > 0);
  const [changeCounter, setChangeCounter] = useState(0);
  const changedRowsRef = useRef<any[]>(Array.isArray(initialChanged) ? [...initialChanged] : []);

  const mirrorExternalRows = React.useCallback((nextRows: any[]) => {
    if (!setExternalRows) return;
    setExternalRows(nextRows);
  }, [setExternalRows]);

  useEffect(() => { allRowsRef.current = allRows; }, [allRows]);

  /**
   * When persistence is enabled, settings is the source of truth for TableChangedRows.
   * We sync it into changedRowsRef.current and then bump changeCounter so the single merge effect applies it.
   */
  useEffect(() => {
    if (!storeUserEdits) return;
    const ctxChangedRows = getNestedValue(settings, ["TableChangedRows", tableName], []);
    const next = Array.isArray(ctxChangedRows) ? [...ctxChangedRows] : [];

    // Avoid unnecessary churn if settings already matches our ref content (best-effort shallow compare)
    const prev = changedRowsRef.current;
    const same =
      Array.isArray(prev) &&
      prev.length === next.length &&
      prev.every((r, i) => tableHelper.getRowIdFromPath(r, idPath) === tableHelper.getRowIdFromPath(next[i], idPath));
    if (!same) {
      changedRowsRef.current = next;
      setHasUnsavedRows(next.length > 0);
      setChangeCounter((prevCounter) => prevCounter + 1);
    }
  }, [settings, storeUserEdits, tableName, idPath]);

  const [pinnedRows, setPinnedRows] = React.useState<{ top: GridRowModel[]; bottom: GridRowModel[] }>({ top: [], bottom: [] });

  const addAndPinMultiEditRow = () => {
    executeAddAndPinMultiEditRow(pinnedRows, setPinnedRows, idPath);
  };
  const removeMultiEditRow = () => {
    executeRemoveMultiEditRow(pinnedRows, setPinnedRows, idPath);
  };

  const updateChangedRowsSetting = React.useCallback(() => {
    if (!storeUserEdits) return;
    setSetting(["TableChangedRows", tableName ?? "UnNamed"], [...changedRowsRef.current]);
  }, [storeUserEdits, setSetting, tableName]);

  const processRowUpdate = React.useCallback(
    (updatedRow: any, originalRow: any) => {
      const committed = updatedRow;

      upsertChangedRowById(changedRowsRef, idPath, committed);

      setHasUnsavedRows(true);
      updateChangedRowsSetting();
      setChangeCounter((prev) => prev + 1);

      const nextRows = replaceRowById(allRowsRef.current, idPath, committed);
      allRowsRef.current = nextRows;
      setAllRows(nextRows);
      mirrorExternalRows(nextRows);

      return committed;
    },
    [idPath, mirrorExternalRows, updateChangedRowsSetting],
  );

  processRowUpdateImplRef.current = processRowUpdate;

  if (!defaultProcessRowUpdateRef.current) {
    defaultProcessRowUpdateRef.current = (updatedRow: any, originalRow: any) => {
      return processRowUpdateImplRef.current?.(updatedRow, originalRow) ?? updatedRow;
    };
  }

  React.useLayoutEffect(() => {
    const defaultHandler = defaultProcessRowUpdateRef.current;
    if (!defaultHandler) return;

    // Only install the default handler if nobody else has wrapped/replaced it.
    // This is important for useTableSelector display-table flows.
    if (processRowUpdateRef.current == null) {
      processRowUpdateRef.current = defaultHandler;
    }
    return () => {
      if (processRowUpdateRef.current === defaultHandler) {
        processRowUpdateRef.current = null;
      }
    };
  }, []);

  const addRow = (newRow) => {
    executeAddRow(newRow, idPath, setHasUnsavedRows, updateChangedRowsSetting, newRowToEditRef, changedRowsRef, setChangeCounter, apiRef);
  };

  const deleteRow = (rowId) => {
    executeDeleteRow(rowId, originalRows, idPath, setHasUnsavedRows, updateChangedRowsSetting, changedRowsRef, setChangeCounter);
  };

  const discardRowChanges = (id) => {
    executeDiscardRowChanges(id, idPath, setHasUnsavedRows, updateChangedRowsSetting, changedRowsRef, setChangeCounter);
  };

  const discardAllChanges = () => {
    executeDiscardChanges(setSetting, tableName, setHasUnsavedRows, changedRowsRef, setChangeCounter, storeUserEdits);
  };

  const commitChanges = () => {
    const currentRowsMap = mapRowsById(allRows, idPath);
    changedRowsRef.current.forEach((changedRow) => {
      const rowId = tableHelper.getRowIdFromPath(changedRow, idPath);
      if (changedRow.deleted) currentRowsMap.delete(rowId);
      else currentRowsMap.set(rowId, changedRow);
    });

    const updatedRows = Array.from(currentRowsMap.values());
    allRowsRef.current = updatedRows;
    setAllRows(updatedRows);
    mirrorExternalRows(updatedRows);

    if (storeUserEdits) setSetting(["TableChangedRows", tableName], []);

    changedRowsRef.current = [];
    setHasUnsavedRows(false);
    setChangeCounter((prev) => prev + 1);
  };

  const handleCellEditEnd = React.useCallback((params, event, details) => {
    // Normal row/cell edits must be committed through processRowUpdate.
    // rowEditStop does not reliably contain the committed row value.
    if (params.id !== "multi-edit-row") return;
    executeHandleCellEditEnd(params, apiRef, idPath, allRows, setAllRows, rowSelectionModel, setSetting, setHasUnsavedRows, updateChangedRowsSetting, setChangeCounter, changedRowsRef, mirrorExternalRows,);
  }, [apiRef, idPath, allRows, rowSelectionModel, setSetting, updateChangedRowsSetting, mirrorExternalRows,]);

  /**
   * Single source of truth for applying edits to the grid:
   * Always merge originalRows with the CURRENT changedRowsRef.current.
   * Clearing changedRowsRef.current naturally reverts the UI on next tick.
   */
  useEffect(() => { mergeRowsAndUpdate(originalRows, changedRowsRef.current, idPath, allRows, setAllRows, apiRef, mirrorExternalRows); }, [originalRows, changeCounter]);

  useEffect(() => { setEditRowMode(newRowToEditRef, setRowModes); }, [allRows]);

  useEffect(() => {
    const api = apiRef?.current;
    if (!api || typeof api.subscribeEvent !== "function") return;
    return api.subscribeEvent("rowEditStop", handleCellEditEnd);
  }, [apiRef, handleCellEditEnd]);

  return { allRows, changedRowsRef, hasUnsavedRows, discardAllChanges, commitChanges, discardRowChanges, addRow, rowModes, setRowModes, addAndPinMultiEditRow, removeMultiEditRow, pinnedRows, deleteRow, processRowUpdateRef, };
};

/**
 * Immutable helpers and guards
 */
const mapRowsById = (rows, idPath) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  return new Map(
    safeRows.map((row) => {
      const rowId = tableHelper.getRowIdFromPath(row, idPath);
      return [rowId, row];
    }),
  );
};

const replaceRowById = (rows: any[], idPath: string[], replacement: any) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const replacementId = tableHelper.getRowIdFromPath(replacement, idPath);
  let hit = false;
  const next = safeRows.map((row) => {
    if (tableHelper.getRowIdFromPath(row, idPath) === replacementId) {
      hit = true;
      return replacement;
    }
    return row;
  });
  return hit ? next : [...next, replacement];
};

const generateUniqueNegativeId = () => {
  const timestamp = new Date().getTime();
  const randomFactor = Math.floor(Math.random() * 1000);
  return -(timestamp + randomFactor);
};

const normalizeParamsValuesIntoRow = (target: any, values: Record<string, any>) => {
  const edited = JSON.parse(JSON.stringify(target));
  Object.entries(values).forEach(([key, value]) => {
    const [baseKey, nestedKey] = key.split("_");
    if (baseKey && nestedKey) {
      edited[baseKey] = edited[baseKey] ?? {};
      edited[baseKey][nestedKey] = value;
    } else {
      edited[key] = value;
    }
  });
  return edited;
};

const setEditRowMode = (newRowToEditRef, setRowModes) => {
  if (newRowToEditRef.current != null) {
    setRowModes((prevModes) => ({ ...prevModes, [newRowToEditRef.current]: { mode: "Edit", fieldToFocus: "Manufacturer_Value" }, }));
    newRowToEditRef.current = null;
  }
};

const mergeRowsAndUpdate = (originalRows, changedRows, idPath, allRows, setAllRows, apiRef, mirrorExternalRows?) => {
  const originalRowsMap = mapRowsById(originalRows, idPath);
  const changedRowsMap = mapRowsById(changedRows, idPath);
  const newRows = [];
  changedRowsMap.forEach((row, rowId) => {
    if (!originalRowsMap.has(rowId)) {
      newRows.push(row);
    } else {
      originalRowsMap.set(rowId, row);
    }
  });
  const updatedRows = Array.from(originalRowsMap.values());
  const combinedRows = [...newRows, ...updatedRows];
  if (combinedRows.length !== allRows.length || combinedRows.some((row, index) => row !== allRows[index])) {
    setAllRows(combinedRows);
    if (apiRef?.current?.updateRows !== undefined) apiRef.current.updateRows(combinedRows);
    mirrorExternalRows?.(combinedRows);
  }
};

/**
 * Immutable upsert helper for changedRowsRef.current
 */
const upsertChangedRowById = (changedRowsRef, idPath, row) => {
  const id = tableHelper.getRowIdFromPath(row, idPath);
  const next = [...changedRowsRef.current];
  const idx = next.findIndex((r) => tableHelper.getRowIdFromPath(r, idPath) === id);
  if (idx >= 0) next[idx] = row;
  else next.push(row);
  changedRowsRef.current = next;
};

/**
 * Multi-edit row update
 */
const updateSelectedRowsBasedOnEdit = (params, allRows, setAllRows, rowSelectionModel, idPath, apiRef, setUserEdits, setHasUnsavedRows, updateChangedRowsSetting, setChangeCounter, changedRowsRef, mirrorExternalRows?,) => {
  const selectedRowIds = rowSelectionModel ?? [];
  const selectedRows = allRows.filter((row) => selectedRowIds.includes(tableHelper.getRowIdFromPath(row, idPath)));
  let updatedRows = [...allRows];
  selectedRows.forEach((selectedRow) => {
    const editedRow = normalizeParamsValuesIntoRow(selectedRow, params.values);
    updatedRows = updatedRows.map((row) => tableHelper.getRowIdFromPath(row, idPath) === tableHelper.getRowIdFromPath(editedRow, idPath) ? editedRow : row,);
    upsertChangedRowById(changedRowsRef, idPath, editedRow);
    setHasUnsavedRows(true);
    updateChangedRowsSetting();
    setChangeCounter((prev) => prev + 1);
  });
  apiRef?.current?.updateRows(updatedRows);
  mirrorExternalRows?.(updatedRows);
};

/**
 * Single row edit end.
 *
 * Kept for the legacy multi-edit pipeline. Normal row/cell commits should now
 * go through processRowUpdate instead, because that is where MUI gives us the
 * committed row.
 */
const handleSingleCellEditEnd = (params, originalRow, idPath, setHasUnsavedRows, updateChangedRowsSetting, setChangeCounter, changedRowsRef, apiRef,) => {
  const current = apiRef?.current?.getRow?.(params.id) ?? params?.row ?? originalRow;
  const editedRow = { ...current };
  const field = params.field ?? "";
  const [baseKey, nestedKey] = field.split("_");
  if (baseKey && nestedKey && editedRow[baseKey]) {
    editedRow[baseKey] = { ...(editedRow[baseKey] ?? {}) };
    editedRow[baseKey][nestedKey] = current?.[baseKey]?.[nestedKey];
  } else if (field) {
    editedRow[field] = current?.[field];
  }
  upsertChangedRowById(changedRowsRef, idPath, editedRow);
  setHasUnsavedRows(true);
  updateChangedRowsSetting();
  setChangeCounter((prev) => prev + 1);
};

const executeAddAndPinMultiEditRow = (pinnedRows, setPinnedRows, idPath) => {
  const uniqueId = "multi-edit-row";
  let multiEditRow = {};
  tableHelper.updateRowIdWithPath(multiEditRow, uniqueId, idPath);
  if (!pinnedRows.top.some((row) => tableHelper.getRowIdFromPath(row, idPath) === uniqueId)) {
    setPinnedRows((prev) => ({ ...prev, top: [multiEditRow, ...prev.top] }));
  }
};

const executeRemoveMultiEditRow = (pinnedRows, setPinnedRows, idPath) => {
  const uniqueId = "multi-edit-row";
  setPinnedRows((prev) => ({ ...prev, top: prev.top.filter((row) => tableHelper.getRowIdFromPath(row, idPath) !== uniqueId), bottom: prev.bottom.filter((row) => tableHelper.getRowIdFromPath(row, idPath) !== uniqueId), }));
};

const executeAddRow = (newRow, idPath, setHasUnsavedRows, updateChangedRows, newRowToEditRef, changedRowsRef, setChangeCounter, apiRef,) => {
  const uniqueNegativeId = generateUniqueNegativeId();
  tableHelper.updateRowIdWithPath(newRow, uniqueNegativeId, idPath);
  newRowToEditRef.current = uniqueNegativeId;
  changedRowsRef.current = [...changedRowsRef.current, newRow];
  if (apiRef?.current && Array.isArray(newRow.path) && newRow.path.length > 1) {
    const parentId = newRow.path[newRow.path.length - 2];
    setTimeout(() => {
      try {
        apiRef.current.setRowChildrenExpansion(parentId, true);
      } catch (e) {
        //console.warn("Could not expand parent row after insertion", e);
      }
    });
  }
  setHasUnsavedRows(true);
  updateChangedRows();
  setChangeCounter((prev) => prev + 1);
};

const executeDeleteRow = (rowId, originalRows, idPath, setHasUnsavedRows, updateChangedRows, changedRowsRef, setChangeCounter,) => {
  const markRowAsDeleted = (row) => ({ ...row, deleted: true });

  const originalRowIndex = originalRows.findIndex((row) => tableHelper.getRowIdFromPath(row, idPath) === rowId);

  let deletedRow;
  const existingIndex = changedRowsRef.current.findIndex((row) => tableHelper.getRowIdFromPath(row, idPath) === rowId);

  if (existingIndex !== -1) {
    deletedRow = markRowAsDeleted(changedRowsRef.current[existingIndex]);
    const next = [...changedRowsRef.current];
    next[existingIndex] = deletedRow;
    changedRowsRef.current = next;
  } else if (originalRowIndex !== -1) {
    const originalRow = originalRows[originalRowIndex];
    deletedRow = markRowAsDeleted(originalRow);
    changedRowsRef.current = [...changedRowsRef.current, deletedRow];
  } else {
    console.error("Row not found in originalRows or changedRows", rowId);
    return;
  }

  const allRows = [...originalRows, ...changedRowsRef.current];
  const childRows = allRows.filter((row) => row.path?.includes(rowId));

  childRows.forEach((childRow) => {
    const childId = tableHelper.getRowIdFromPath(childRow, idPath);
    const childRowIndex = changedRowsRef.current.findIndex((changedRow) => tableHelper.getRowIdFromPath(changedRow, idPath) === childId);
    if (childRowIndex !== -1) {
      const next = [...changedRowsRef.current];
      next[childRowIndex] = markRowAsDeleted(next[childRowIndex]);
      changedRowsRef.current = next;
    } else {
      changedRowsRef.current = [...changedRowsRef.current, markRowAsDeleted(childRow)];
    }
  });

  setHasUnsavedRows(true);
  updateChangedRows();
  setChangeCounter((prev) => prev + 1);
};

const executeDiscardRowChanges = (id, idPath, setHasUnsavedRows, updateChangedRows, changedRowsRef, setChangeCounter) => {
  const idRegex = new RegExp(`${id}$`);
  changedRowsRef.current = changedRowsRef.current.filter((row) => !row.path?.some((pathSegment) => idRegex.test(pathSegment)) && tableHelper.getRowIdFromPath(row, idPath) !== id,);
  setHasUnsavedRows(changedRowsRef.current.length > 0);
  updateChangedRows();
  setChangeCounter((prev) => prev + 1);
};

const executeDiscardChanges = (setUserEdits, tableName, setHasUnsavedRows, changedRowsRef, setChangeCounter, storeUserEdits: boolean) => {
  if (storeUserEdits) setUserEdits(["TableChangedRows", tableName ?? "UnNamed"], []);
  changedRowsRef.current = [];
  setHasUnsavedRows(false);
  setChangeCounter((prev) => prev + 1);
};

const executeHandleCellEditEnd = (params, apiRef, idPath, allRows, setAllRows, rowSelectionModel, setUserEdits, setHasUnsavedRows, updateChangedRowsSetting, setChangeCounter, changedRowsRef, mirrorExternalRows?,) => {
  const isMultiEditRow = params.id === "multi-edit-row";
  if (isMultiEditRow) {
    updateSelectedRowsBasedOnEdit(params, allRows, setAllRows, rowSelectionModel, idPath, apiRef, setUserEdits, setHasUnsavedRows, updateChangedRowsSetting, setChangeCounter, changedRowsRef, mirrorExternalRows,);
    return;
  }
  const originalRow = apiRef?.current.getRow(params.id);
  if (originalRow) {
    handleSingleCellEditEnd(params, originalRow, idPath, setHasUnsavedRows, updateChangedRowsSetting, setChangeCounter, changedRowsRef, apiRef);
  }
};
